import { t } from '@reos/i18n';
import { CASE_SCAN_MAX_PAGES, getCase, listCases } from './cases-api.js';
import { CASE_NAME_TEXTS, caseDisplayName, isInternalIdTitle, type CaseNameTexts } from './case-title.js';
import { fetchPropertyAddresses, fetchPropertyFacts } from './properties.js';

/**
 * Adresa nemovitosti/zakazky pro tabulky nakladu (krok O6a, polozka 2).
 * `GET /v1/cases` uz vraci `title` (= addressQuery zalozene zakazky, viz
 * `lib/audit-api.ts`) a `propertyId` — zadny novy backendovy join, jen
 * mapovani z jiz existujiciho seznamu zakazek.
 *
 * KROK D1 bod 4: nazev jde PRES `caseDisplayName` (SSOT z kroku O8), takze ve
 * vyberu zakazky u nove smlouvy ani v prehledu smluv nemuze skoncit interni id
 * („Nemovitost 01a05672", holy UUID). Zakazkam se zastupnym nazvem se nejdriv
 * dohleda adresa nemovitosti; teprve kdyz neni, zbyde „Bez nazvu".
 * Do tohoto kroku se tady `c.title` vracel tak, jak prisel ze serveru — a
 * v demu i v ostrem provozu se tim do seznamu dostavalo interni id.
 */
/** `MAX_PAGE_LIMIT` z `@reos/schemas listQuerySchema` - vyssi hodnota vraci 400 (ADM1 bod 3). */
const PAGE_LIMIT = 100;

/** Krok APP2 bod 1: texty nahradniho nazvu jsou jedna kopie v `case-title.ts`. */
const NAME_TEXTS: CaseNameTexts = CASE_NAME_TEXTS;

type CaseAddressIndex = {
  readonly byPropertyId: ReadonlyMap<string, string>;
  readonly byCaseId: ReadonlyMap<string, string>;
};

/**
 * Krok NOC1 bod 2 — PRICINA KVOTY: JEDNA OBRAZOVKA, OSM SKENU ZAKAZEK.
 *
 * Nocni QA 13. 10. 2026 nasla na `/smlouvy` (390×844) pad do error boundary
 * s hlaskou „Příliš mnoho požadavků" z `GET /v1/cases`. Opakovani podle
 * `Retry-After` (`lib/rate-limit-retry.ts`) je zachrana, tohle je prícina:
 * jedno volani indexu STRANKUJE az `CASE_SCAN_MAX_PAGES` (100) dotazu a
 * nevolá ho jedno misto, ale osm nezavislych efektu — `ContractsScreen`,
 * `NewContractView`, `ContractEditorScreen` (DVAKRAT), `invoices`, `klienti`
 * a tri obrazovky nakladu. Mountuji se v temze ticku a kazdy si stahl vlastni
 * kopii teze tabulky.
 *
 * Soubezna volani proto sdileji JEDEN beh. Vedome se neskladuje vysledek po
 * dobehnuti (zadna TTL cache): index nese nazvy zakazek a zalozena zakazka
 * se musi objevit hned, ne az vyprsi okno. Ziskem je presne to, na co QA
 * narazila — soucet dotazu jedne obrazovky klesne na jeden sken.
 */
let inFlight: Promise<CaseAddressIndex> | null = null;

/**
 * Krok APP2 bod 1 — ROZSAH INDEXU: CO OBRAZOVKA SKUTECNE POTREBUJE.
 *
 * Bez rozsahu se index stahuje cely (puvodni chovani). Obrazovky, ktere uz
 * ZNAJI mnozinu nemovitosti (tabulky nakladu) nebo hledaji podle textu (vyber
 * zakazky u nove smlouvy), si vyzadaji JEDEN dotaz — ne sken kancelare po
 * stovkach. Pri 343 zakazkach MV to byly ctyri dotazy na obrazovku, v DEMO
 * kancelari desitky a kvota (429).
 */
export type CaseAddressScope =
  | { readonly propertyIds: readonly string[] }
  | { readonly q: string };

export function fetchCaseAddressIndex(scope?: CaseAddressScope): Promise<CaseAddressIndex> {
  if (scope !== undefined && 'propertyIds' in scope) return loadForProperties(scope.propertyIds);
  if (scope !== undefined) return loadForQuery(scope.q);
  if (inFlight !== null) return inFlight;
  // Selhani se nesmi zapamatovat — dalsi volajici ma pravo na novy pokus.
  const run = loadCaseAddressIndex().finally(() => {
    if (inFlight === run) inFlight = null;
  });
  inFlight = run;
  return run;
}

/** Index pro ZNAMA id nemovitosti — jeden dotaz (`?propertyIds=`), zadny sken. */
async function loadForProperties(propertyIds: readonly string[]): Promise<CaseAddressIndex> {
  const ids = [...new Set(propertyIds)].filter((id) => id !== '');
  if (ids.length === 0) return { byPropertyId: new Map(), byCaseId: new Map() };
  const result = await listCases({ propertyIds: ids, limit: PAGE_LIMIT });
  return indexOf(result.items);
}

/**
 * Index podle HLEDANEHO TEXTU — tataz jedna cesta jako nabidka zakazky
 * u faktury (`searchCasesByName`, krok PROP3B), aby obe nabidky skladaly
 * jmena zakazek stejne.
 */
async function loadForQuery(q: string): Promise<CaseAddressIndex> {
  const byCaseId = await searchCasesByName(q);
  // Nazvy podle nemovitosti hledani nevraci — tady je nikdo nepotrebuje
  // (vyber zakazky pracuje s `byCaseId`).
  return { byPropertyId: new Map(), byCaseId };
}

/** Nejkratsi dotaz, se kterym ma hledani smysl (tentyz strop jako `q` v API). */
export const CASE_SEARCH_MIN_LENGTH = 2;

/**
 * Krok PROP3B (oprava e2e 1) — ZAKAZKA PODLE NAZVU SE HLEDA NA SERVERU.
 *
 * Vyber zakazky u faktury („Ze zakázky") dosud stahl `fetchCaseAddressIndex()`,
 * tedy CELY seznam zakazek kancelare, a hledal v nem v prohlizeci. V DEMO
 * kancelari to 15. 10. 2026 bylo 54 dotazu po 100 zakazkach, ~6 minut a
 * posledni dotaz skoncil na kvote (429) — nabidka pak zustala PRAZDNA a
 * makler, ktery napsal adresu sve zakazky, nedostal zadnou odpoved ani zadny
 * duvod (e2e S18c proti preview). Kancelar navic kazdym dnem roste, takze
 * sken se jen prodluzuje.
 *
 * Hleda `GET /v1/cases?q=` (jeden dotaz) a nazev skladá TOUTEZ funkci jako
 * index (`caseDisplayName`), aby obe cesty nabizely stejna jmena zakazek.
 * Index zustava tam, kde je potreba JMENO ZNAMEHO ID (radky nakladu, prehled
 * smluv) — tam se neceka od uzivatele zadny dotaz.
 *
 * Krok TOKYFIX3 (prekazka P7/2) — NEJNOVEJSI ZAKAZKY PRVNI. Server razi
 * `created_at asc` a napoveda ukazuje jen prvnich `CASE_OPTIONS_LIMIT` (8)
 * polozek. V mereni 16. 10. 2026 vratil server na adresu „Vinohradská
 * 2446/54" JEDENACT zakazek a ta, kterou makleřka prave zalozila, byla
 * JEDENACTA — nabidka ji proto neukazala vubec. Hledajici clovek miri na
 * zakazku, kterou zalozil nedavno, takze oriznout se musi ty stare.
 */
export async function searchCasesByName(query: string): Promise<ReadonlyMap<string, string>> {
  const q = query.trim();
  const found = new Map<string, string>();
  if (q.length < CASE_SEARCH_MIN_LENGTH) return found;
  const result = await listCases({ q, limit: PAGE_LIMIT });
  // Zakazkam se zastupnym nazvem se dohleda adresa — stejne jako v indexu.
  const bezNazvu = result.items.filter((c) => isInternalIdTitle(c.title) && c.propertyId !== null);
  const addressByProperty =
    bezNazvu.length === 0
      ? new Map<string, string>()
      : await fetchPropertyAddresses(bezNazvu.map((c) => c.propertyId as string));
  for (const c of [...result.items].reverse()) {
    found.set(c.id, caseDisplayName(c, addressByProperty, NAME_TEXTS).text);
  }
  return found;
}

/** Klic cisla jednotky v registru `FACT_KEYS` — pise ho pruvodce i nabor. */
const UNIT_NUMBER_FACT_KEY = 'unit_number';

/**
 * Krok P8F6 bod 4 — NAZEV JEDNE ZAKAZKY: ADRESA A JEDNOTKA, NIKDY ID.
 *
 * „Nová smlouva" otevrena ZE zakazky nemela nazev odkud vzit — index se v tom
 * pripade zamerne nestahuje (NOC1 bod 2), takze krok „Ke které zakázce"
 * ukazoval holé UUID (nalez P8-N13). Tady jsou tri dotazy na JEDNU zakazku
 * misto skenu cele kancelare: zakazka, adresa jeji nemovitosti a fakta karty.
 *
 * Nazev sklada TATAZ `caseDisplayName` jako index i hledani; jednotka se bere
 * z faktu `unit_number`, tedy z TEHOZ klice, jaky zapisuje pruvodce (P8F6
 * bod 1) a nabor. Kdyz karta jednotku nenese, veta je jen adresa.
 */
export async function caseNameWithUnit(caseId: string): Promise<string> {
  const kase = await getCase(caseId);
  const addressByProperty =
    kase.propertyId === null
      ? new Map<string, string>()
      : await fetchPropertyAddresses([kase.propertyId]);
  const name = caseDisplayName(kase, addressByProperty, NAME_TEXTS).text;
  if (kase.propertyId === null) return name;
  // Chybejici pravo na fakta karty nazev nezahodi — adresa uz je lidska.
  const facts = await fetchPropertyFacts(kase.propertyId).catch(() => []);
  const unit = facts.find((fact) => fact.factKey === UNIT_NUMBER_FACT_KEY)?.value;
  if (typeof unit !== 'string' || unit.trim() === '') return name;
  return t('webAdmin.caseName.withUnit', { locale: 'cs', values: { name, unit: unit.trim() } });
}

async function loadCaseAddressIndex(): Promise<CaseAddressIndex> {
  const rows: { readonly id: string; readonly title: string; readonly propertyId: string | null }[] =
    [];
  let cursor: string | null = null;
  // Strop je `CASE_SCAN_MAX_PAGES` (`lib/cases-api.ts`) — strankovani je
  // vzestupne, takze nizky strop zahodi NEJNOVEJSI zakazky (e2e S18c).
  for (let page = 0; page < CASE_SCAN_MAX_PAGES; page += 1) {
    const result = await listCases({ limit: PAGE_LIMIT, ...(cursor !== null ? { cursor } : {}) });
    for (const c of result.items) rows.push({ id: c.id, title: c.title, propertyId: c.propertyId });
    if (result.nextCursor === null) break;
    cursor = result.nextCursor;
  }

  return indexOf(rows);
}

/**
 * Nazvy zakazek z nactenych radku — JEDNO misto pro cely index i pro jeho
 * zuzene rozsahy (krok APP2 bod 1). Dve kopie by se rozesly prave v tom, co
 * resil krok D1: co se stane se zakazkou bez pouzitelneho nazvu.
 */
async function indexOf(
  rows: readonly { readonly id: string; readonly title: string; readonly propertyId: string | null }[],
): Promise<CaseAddressIndex> {
  // Adresy se dohledavaji JEN pro zakazky se zastupnym nazvem — u ostatnich
  // je nazev z dat a dalsi dotaz by byl zbytecny.
  const bezNazvu = rows.filter((r) => isInternalIdTitle(r.title) && r.propertyId !== null);
  const addressByProperty =
    bezNazvu.length === 0
      ? new Map<string, string>()
      : await fetchPropertyAddresses(bezNazvu.map((r) => r.propertyId as string));

  const byPropertyId = new Map<string, string>();
  const byCaseId = new Map<string, string>();
  for (const row of rows) {
    const name = caseDisplayName(row, addressByProperty, NAME_TEXTS).text;
    byCaseId.set(row.id, name);
    if (row.propertyId !== null && !byPropertyId.has(row.propertyId)) {
      byPropertyId.set(row.propertyId, name);
    }
  }
  return { byPropertyId, byCaseId };
}
